/**
 * Zwei Spalten der geöffneten Akte. Links die feste Folge, rechts ein Gegenstand.
 * Außenklick schließt nichts. Keine Element-Räume in dieser Leiste.
 */

import React from 'react';
import { PortalShortcutHint } from '../PortalShortcutHint';
import { usePortalShortcutAria } from '../portalShortcutContext';
import {
  portalAkteRahmenSectionLabel,
  type PortalAkteRahmenSectionId,
} from '../portalAkteRahmen';

const ROW =
  'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium';
const ROW_ACTIVE = 'bg-violet-500/15 text-white ring-1 ring-violet-400/30';
const ROW_IDLE = 'text-slate-200 hover:bg-white/[0.04] hover:text-white';

export type PortalAkteRahmenChromeProps = {
  akteTitle: string;
  sectionId: PortalAkteRahmenSectionId;
  sections: ReadonlyArray<{ id: PortalAkteRahmenSectionId; label: string }>;
  onBack: () => void;
  onSection: (id: PortalAkteRahmenSectionId) => void;
  children: React.ReactNode;
};

export const PortalAkteRahmenChrome: React.FC<PortalAkteRahmenChromeProps> = ({
  akteTitle,
  sectionId,
  sections,
  onBack,
  onSection,
  children,
}) => {
  const zurueckShortcut = usePortalShortcutAria('zurueck');
  const rightLabel = portalAkteRahmenSectionLabel(sectionId);

  return (
    <section
      data-testid="portal-akte-rahmen"
      className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
      aria-label={`Akte ${akteTitle}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] px-3 py-3">
        <div className="min-w-0">
          <p className="m-0 text-sm text-slate-400">Akte</p>
          <p className="m-0 truncate text-sm font-semibold text-slate-100">{akteTitle}</p>
        </div>
        <button
          type="button"
          data-testid="portal-akte-rahmen-back"
          data-portal-shortcut="zurueck"
          aria-keyshortcuts={zurueckShortcut}
          title={zurueckShortcut}
          onClick={onBack}
          className="inline-flex shrink-0 items-center justify-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500"
        >
          Alle Akten
          <PortalShortcutHint id="zurueck" className="border-white/25 text-white/70" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <nav
          aria-label="Abschnitte dieser Akte"
          className="w-full shrink-0 overflow-y-auto border-b border-white/[0.06] p-2 lg:w-56 lg:border-b-0 lg:border-r"
        >
          <ul className="m-0 list-none space-y-1 p-0">
            {sections.map((section) => {
              const selected = section.id === sectionId;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    role="tab"
                    data-testid={`portal-akte-rahmen-section-${section.id}`}
                    aria-selected={selected}
                    onClick={() => onSection(section.id)}
                    className={`${ROW} ${selected ? ROW_ACTIVE : ROW_IDLE}`}
                  >
                    {section.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" role="tabpanel" aria-label={rightLabel}>
          <h3 className="m-0 shrink-0 border-b border-white/[0.06] px-3 py-3 text-sm font-semibold text-slate-100">
            {rightLabel}
          </h3>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
        </div>
      </div>
    </section>
  );
};
